import React, { useState, useEffect } from 'react';
import api from '../api';

interface Warehouse {
  id: number;
  code: string;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  manager_name?: string;
  contact_email?: string;
  contact_phone?: string;
  is_active: boolean;
  is_default?: boolean;
}

interface WarehouseSummary {
  warehouse_id: number;
  warehouse_name: string;
  total_products: number;
  total_quantity: number;
  total_value: number;
}

const WarehouseManager: React.FC = () => {
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [summaries, setSummaries] = useState<WarehouseSummary[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingWarehouse, setEditingWarehouse] = useState<Warehouse | null>(null);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [viewingWarehouse, setViewingWarehouse] = useState<Warehouse | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    address: '',
    city: '',
    state: '',
    country: '',
    postal_code: '',
    manager_name: '',
    contact_email: '',
    contact_phone: '',
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize]);

  useEffect(() => {
    const handleClickOutside = () => setOpenMenuId(null);
    if (openMenuId !== null) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openMenuId]);

  const fetchData = async () => {
    try {
      const skip = (currentPage - 1) * pageSize;
      const [warehousesRes, summariesRes] = await Promise.all([
        api.get('/inventory/warehouses/', { params: { skip, limit: pageSize } }),
        api.get('/inventory/warehouse-summary')
      ]);
      setWarehouses(warehousesRes.data);
      setTotalCount(warehousesRes.data.length);
      setSummaries(summariesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.code || !formData.name) {
      alert('Please fill in code and name');
      return;
    }

    try {
      if (editingWarehouse) {
        await api.put(`/inventory/warehouses/${editingWarehouse.id}`, formData);
        alert('Warehouse updated successfully');
      } else {
        await api.post('/inventory/warehouses/', formData);
        alert('Warehouse created successfully');
      }
      setShowForm(false);
      setEditingWarehouse(null);
      setFormData({
        code: '',
        name: '',
        address: '',
        city: '',
        state: '',
        country: '',
        postal_code: '',
        manager_name: '',
        contact_email: '',
        contact_phone: '',
        is_active: true
      });
      fetchData();
    } catch (error: any) {
      console.error('Error saving warehouse:', error);
      alert(error.response?.data?.detail || 'Failed to save warehouse');
    }
  };

  const handleEdit = (warehouse: Warehouse) => {
    setEditingWarehouse(warehouse);
    setFormData({
      code: warehouse.code,
      name: warehouse.name,
      address: warehouse.address || '',
      city: warehouse.city || '',
      state: warehouse.state || '',
      country: warehouse.country || '',
      postal_code: warehouse.postal_code || '',
      manager_name: warehouse.manager_name || '',
      contact_email: warehouse.contact_email || '',
      contact_phone: warehouse.contact_phone || '',
      is_active: warehouse.is_active
    });
    setShowForm(true);
    setOpenMenuId(null);
  };

  const handleToggleActive = async (warehouse: Warehouse) => {
    try {
      await api.put(`/inventory/warehouses/${warehouse.id}`, {
        ...warehouse,
        is_active: !warehouse.is_active
      });
      fetchData();
      setOpenMenuId(null);
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleSetDefault = async (warehouse: Warehouse) => {
    try {
      await api.put(`/inventory/warehouses/${warehouse.id}/set-default`);
      fetchData();
      setOpenMenuId(null);
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Failed to set as default');
    }
  };

  const handleDeleteClick = (id: number) => {
    setDeleteId(id);
    setShowDeleteConfirm(true);
    setOpenMenuId(null);
  };

  const getSummary = (warehouseId: number) => {
    return summaries.find(s => s.warehouse_id === warehouseId);
  };

  const confirmDelete = async () => {
    if (confirmText !== 'confirm' || !deleteId) return;
    
    try {
      await api.delete(`/inventory/warehouses/${deleteId}`);
      fetchData();
      setShowDeleteConfirm(false);
      setDeleteId(null);
      setConfirmText('');
    } catch (error: any) {
      console.error('Error deleting warehouse:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to delete warehouse';
      alert(errorMsg);
    }
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>;
  }

  return (
    <div>
      <div className="card-header">
        <h2 className="card-title">Warehouse Management</h2>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          <span className="btn-icon">➕</span>
          New Warehouse
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
        {warehouses.length === 0 ? (
          <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem', color: '#64748b' }}>
            No warehouses found. Create your first warehouse.
          </div>
        ) : (
          warehouses.map(warehouse => {
            const summary = getSummary(warehouse.id);
            return (
              <div key={warehouse.id} style={{
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '1rem',
                background: 'white',
                position: 'relative'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '0.25rem' }}>
                      {warehouse.name}
                    </h3>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>
                      {warehouse.code}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <span className={`status-badge ${warehouse.is_active ? 'status-active' : 'status-inactive'}`} style={{ fontSize: '11px', padding: '2px 8px' }}>
                      {warehouse.is_active ? 'Active' : 'Inactive'}
                    </span>
                    {warehouse.is_default && (
                      <span style={{ fontSize: '11px', padding: '2px 8px', background: '#dbeafe', color: '#1d4ed8', borderRadius: '4px', fontWeight: 600 }}>
                        ⭐ Default
                      </span>
                    )}
                    <div style={{ position: 'relative' }}>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuId(openMenuId === warehouse.id ? null : warehouse.id);
                        }}
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          cursor: 'pointer', 
                          fontSize: '18px',
                          padding: '4px 8px',
                          color: '#64748b'
                        }}
                      >
                        ⋮
                      </button>
                      {openMenuId === warehouse.id && (
                        <div style={{
                          position: 'absolute',
                          right: 0,
                          top: '100%',
                          background: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: '4px',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                          zIndex: 10,
                          minWidth: '150px'
                        }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setViewingWarehouse(warehouse);
                              setOpenMenuId(null);
                            }}
                            style={{
                              width: '100%',
                              padding: '0.5rem 1rem',
                              border: 'none',
                              background: 'none',
                              textAlign: 'left',
                              cursor: 'pointer',
                              fontSize: '13px'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                          >
                            👁️ View
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(warehouse);
                            }}
                            style={{
                              width: '100%',
                              padding: '0.5rem 1rem',
                              border: 'none',
                              background: 'none',
                              textAlign: 'left',
                              cursor: 'pointer',
                              fontSize: '13px'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleActive(warehouse);
                            }}
                            style={{
                              width: '100%',
                              padding: '0.5rem 1rem',
                              border: 'none',
                              background: 'none',
                              textAlign: 'left',
                              cursor: 'pointer',
                              fontSize: '13px'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                          >
                            {warehouse.is_active ? '🚫 Deactivate' : '✅ Activate'}
                          </button>
                          {!warehouse.is_default && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSetDefault(warehouse);
                              }}
                              style={{
                                width: '100%',
                                padding: '0.5rem 1rem',
                                border: 'none',
                                background: 'none',
                                textAlign: 'left',
                                cursor: 'pointer',
                                fontSize: '13px'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                            >
                              ⭐ Set as Default
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(warehouse.id);
                            }}
                            style={{
                              width: '100%',
                              padding: '0.5rem 1rem',
                              border: 'none',
                              background: 'none',
                              textAlign: 'left',
                              cursor: 'pointer',
                              fontSize: '13px',
                              color: '#ef4444',
                              borderTop: '1px solid #e2e8f0'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#fef2f2'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '0.5rem' }}>
                  {warehouse.city && warehouse.state ? `${warehouse.city}, ${warehouse.state}` : warehouse.city || warehouse.state || 'No location'}
                </div>

                {summary && (
                  <div className="form-grid-3" style={{ 
                    gap: '0.5rem',
                    padding: '0.75rem',
                    background: '#f8fafc',
                    borderRadius: '4px',
                    marginTop: '0.75rem'
                  }}>
                    <div>
                      <div style={{ fontSize: '10px', color: '#64748b' }}>Products</div>
                      <div style={{ fontSize: '16px', fontWeight: 700 }}>{summary.total_products}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '10px', color: '#64748b' }}>Quantity</div>
                      <div style={{ fontSize: '16px', fontWeight: 700 }}>{summary.total_quantity}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '10px', color: '#64748b' }}>Value</div>
                      <div style={{ fontSize: '16px', fontWeight: 700 }}>₹{summary.total_value.toFixed(0)}</div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Pagination Controls */}
      {warehouses.length > 0 && (
        <div style={{ 
          padding: '1rem 2rem', 
          borderTop: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'white',
          marginTop: '1rem'
        }}>
          <div style={{ fontSize: '14px', color: '#64748b' }}>
            Showing {Math.min((currentPage - 1) * pageSize + 1, totalCount)} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} warehouses
          </div>
          
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <label style={{ fontSize: '14px', color: '#64748b', marginRight: '0.5rem' }}>
              Per page:
            </label>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              style={{
                padding: '0.25rem 0.5rem',
                borderRadius: '4px',
                border: '1px solid #cbd5e1',
                fontSize: '14px'
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>

            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="btn btn-secondary"
              style={{ 
                padding: '0.25rem 0.75rem',
                fontSize: '14px',
                opacity: currentPage === 1 ? 0.5 : 1,
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
              }}
            >
              Previous
            </button>

            <span style={{ fontSize: '14px', color: '#64748b', padding: '0 0.5rem' }}>
              Page {currentPage} of {Math.ceil(totalCount / pageSize)}
            </span>

            <button
              onClick={() => setCurrentPage(prev => Math.min(Math.ceil(totalCount / pageSize), prev + 1))}
              disabled={currentPage >= Math.ceil(totalCount / pageSize)}
              className="btn btn-secondary"
              style={{ 
                padding: '0.25rem 0.75rem',
                fontSize: '14px',
                opacity: currentPage >= Math.ceil(totalCount / pageSize) ? 0.5 : 1,
                cursor: currentPage >= Math.ceil(totalCount / pageSize) ? 'not-allowed' : 'pointer'
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '600px', maxHeight: '90vh', overflow: 'auto' }}>
            <h3>{editingWarehouse ? 'Edit Warehouse' : 'Create Warehouse'}</h3>
            
            <div className="form-grid-2" style={{ gap: '1rem', marginTop: '1.5rem', marginBottom: 0 }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                  Code *
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  placeholder="WH001"
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Main Warehouse"
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                />
              </div>
            </div>

            <div style={{ marginTop: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                Address
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 Main Street"
                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
              />
            </div>

            <div className="form-grid-2" style={{ gap: '1rem', marginTop: '1rem', marginBottom: 0 }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                  City
                </label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                  State/Province
                </label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                />
              </div>
            </div>

            <div className="form-grid-2" style={{ gap: '1rem', marginTop: '1rem', marginBottom: 0 }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                  Country
                </label>
                <input
                  type="text"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                  Postal Code
                </label>
                <input
                  type="text"
                  value={formData.postal_code}
                  onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                />
              </div>
            </div>

            <div style={{ marginTop: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                Manager Name
              </label>
              <input
                type="text"
                value={formData.manager_name}
                onChange={(e) => setFormData({ ...formData, manager_name: e.target.value })}
                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
              />
            </div>

            <div className="form-grid-2" style={{ gap: '1rem', marginTop: '1rem', marginBottom: 0 }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                  Contact Email
                </label>
                <input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                  Contact Phone
                </label>
                <input
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                />
              </div>
            </div>

            <div style={{ marginTop: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
                <span style={{ fontWeight: 600 }}>Active</span>
              </label>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
              <button className="btn btn-primary" onClick={handleSubmit}>
                {editingWarehouse ? 'Update Warehouse' : 'Create Warehouse'}
              </button>
              <button className="btn btn-secondary" onClick={() => {
                setShowForm(false);
                setEditingWarehouse(null);
                setFormData({
                  code: '',
                  name: '',
                  address: '',
                  city: '',
                  state: '',
                  country: '',
                  postal_code: '',
                  manager_name: '',
                  contact_email: '',
                  contact_phone: '',
                  is_active: true
                });
              }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {viewingWarehouse && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '600px' }}>
            <h3>Warehouse Details</h3>
            <div style={{ marginTop: '1.5rem' }}>
              <div className="form-grid-2" style={{ gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '0.25rem' }}>Code</div>
                  <div style={{ fontSize: '14px', fontWeight: 600 }}>{viewingWarehouse.code}</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '0.25rem' }}>Name</div>
                  <div style={{ fontSize: '14px', fontWeight: 600 }}>{viewingWarehouse.name}</div>
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '0.25rem' }}>Address</div>
                <div style={{ fontSize: '14px' }}>
                  {viewingWarehouse.address || '-'}<br />
                  {viewingWarehouse.city && `${viewingWarehouse.city}, `}
                  {viewingWarehouse.state && `${viewingWarehouse.state} `}
                  {viewingWarehouse.postal_code || ''}
                  {viewingWarehouse.country && <><br />{viewingWarehouse.country}</>}
                </div>
              </div>

              <div className="form-grid-2" style={{ gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '0.25rem' }}>Manager</div>
                  <div style={{ fontSize: '14px' }}>{viewingWarehouse.manager_name || '-'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '0.25rem' }}>Status</div>
                  <div>
                    <span className={`status-badge ${viewingWarehouse.is_active ? 'status-active' : 'status-inactive'}`}>
                      {viewingWarehouse.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="form-grid-2" style={{ gap: '1rem', marginBottom: 0 }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '0.25rem' }}>Email</div>
                  <div style={{ fontSize: '14px' }}>{viewingWarehouse.contact_email || '-'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '0.25rem' }}>Phone</div>
                  <div style={{ fontSize: '14px' }}>{viewingWarehouse.contact_phone || '-'}</div>
                </div>
              </div>

              {getSummary(viewingWarehouse.id) && (
                <div style={{ 
                  marginTop: '1.5rem',
                  padding: '1rem',
                  background: '#f8fafc',
                  borderRadius: '4px'
                }}>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '0.5rem' }}>Inventory Summary</div>
                  <div className="form-grid-3" style={{ gap: '1rem' }}>
                    <div>
                      <div style={{ fontSize: '10px', color: '#64748b' }}>Products</div>
                      <div style={{ fontSize: '20px', fontWeight: 700 }}>{getSummary(viewingWarehouse.id)?.total_products}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '10px', color: '#64748b' }}>Quantity</div>
                      <div style={{ fontSize: '20px', fontWeight: 700 }}>{getSummary(viewingWarehouse.id)?.total_quantity}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '10px', color: '#64748b' }}>Value</div>
                      <div style={{ fontSize: '20px', fontWeight: 700 }}>₹{getSummary(viewingWarehouse.id)?.total_value.toFixed(0)}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div style={{ marginTop: '1.5rem' }}>
              <button className="btn btn-secondary" onClick={() => setViewingWarehouse(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Confirm Delete</h3>
            <p>You are about to delete this warehouse. This action cannot be undone.</p>
            <p>Type "confirm" to proceed:</p>
            <input
              type="text"
              className="form-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type confirm"
            />
            <div style={{ marginTop: '1rem', display: 'flex', gap: '8px' }}>
              <button 
                className="btn btn-danger"
                onClick={confirmDelete}
                disabled={confirmText !== 'confirm'}
              >
                Delete Warehouse
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteId(null);
                  setConfirmText('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarehouseManager;
